import './SmallButton.scss';

import * as React from 'react';

import cx from 'classnames';

import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';
import { Link } from '~shared/components/Link/Link';
import { Spinner } from '~shared/components/Spinner';

export type SmallButtonAppearance =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'text'
  | 'grey-outline'
  | 'ghost'
  | 'ghost-destructive';

export interface SmallButtonProps {
  className?: string;
  a11yLabel?: string;
  /** The HTML `type` attribute to apply. */
  type?: 'button' | 'submit' | 'reset';
  /** The style variant of the button. */
  appearance?: SmallButtonAppearance;
  isDestructive?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isHovered?: boolean;
  /** Only applies to default appearance. */
  isOnWhite?: boolean;
  onClick?: (event) => unknown;
  autoFocus?: boolean;
  /** used by our testing frameworks */
  'data-testid'?: string;
  children?: React.ReactNode;
  suffix?: React.ReactNode;
  xPadding?: 0 | 4;
  yPadding?: 0 | 2;
}

interface GenerateButtonClassParams {
  appearance: SmallButtonAppearance;
  className?: string;
  isDisabled: boolean;
  isLoading: boolean;
  isHovered: boolean;
  isOnWhite: boolean;
  xPadding?: 0 | 4;
  yPadding?: 0 | 2;
}

export function generateButtonClass({
  appearance,
  isDisabled,
  isLoading,
  isHovered,
  isOnWhite,
  className,
  xPadding = 4,
  yPadding = 2,
}: GenerateButtonClassParams) {
  return cx(
    'flex group relative focus:z-10 m-0 border-0 rounded-2 text-sm leading-8 font-sans font-600 appearance-none outline-none select-none transition ease-in-out duration-100',
    {
      'bg-white text-grey-900 shadow-narrow-border':
        appearance === 'grey-outline' && !isDisabled,

      'bg-white text-grey-900 is-hovered':
        appearance === 'grey-outline' && isHovered && !isDisabled,

      'bg-white text-grey-200 shadow-narrow-border':
        appearance === 'grey-outline' && isDisabled,

      'bg-white text-red-700 hover:bg-red-100 focus:bg-red-100':
        appearance === 'error' && !isDisabled,

      'bg-grey-50 text-disabled':
        isDisabled &&
        [
          'default',
          'primary',
          'secondary',
          'error',
          'warning',
          'text',
        ].includes(appearance),
      'bg-transparent text-disabled':
        isDisabled && ['ghost', 'ghost-destructive'].includes(appearance),

      'cursor-pointer': !isDisabled,

      'bg-orange-700 text-white hover:bg-orange-800 focus:bg-orange-800':
        appearance === 'warning' && !isDisabled,
      'bg-orange-800 text-white is-hovered':
        appearance === 'warning' && isHovered && !isDisabled,

      'text-cyan-700 hover:bg-cyan-100 focus:bg-cyan-100 hover:text-cyan-800 focus:text-cyan-800':
        appearance === 'default' && !isDisabled,
      'bg-cyan-100 text-cyan-800 is-hovered':
        appearance === 'default' && isHovered && !isDisabled,

      'bg-cyan-50': appearance === 'default' && !isDisabled && isOnWhite,
      'bg-white': appearance === 'default' && !isDisabled && !isOnWhite,

      'text-white bg-cyan-500 hover:bg-cyan-600 focus:bg-cyan-600':
        appearance === 'primary' && !isDisabled,
      'bg-cyan-600 is-hovered':
        appearance === 'primary' && isHovered && !isDisabled,

      'text-grey-700 bg-grey-50 hover:text-grey-800 focus:text-grey-800 hover:bg-grey-100 focus:bg-grey-100':
        appearance === 'secondary' && !isDisabled,
      'text-grey-800 bg-grey-100 is-hovered':
        appearance === 'secondary' && isHovered && !isDisabled,

      'text-cyan-700 bg-transparent hover:text-cyan-800 focus:text-cyan-800':
        appearance === 'text' && !isDisabled,
      'text-cyan-800 is-hovered':
        appearance === 'text' && isHovered && !isDisabled,

      'text-cyan-700 bg-transparent hover:bg-cyan-50 focus:bg-cyan-50':
        appearance === 'ghost' && !isDisabled,
      'text-cyan-600 bg-cyan-50 is-hovered':
        appearance === 'ghost' && isHovered && !isDisabled,

      'text-red-800 bg-white hover:text-red-800 focus:text-red-800':
        appearance === 'ghost-destructive' && !isDisabled,
      'px-0': xPadding === 0,
      'py-0': yPadding === 0,
      'px-4': xPadding === 4 || xPadding === null,
      'py-2': yPadding === 2 || yPadding === null,

      [className as string]: !!className,
      'cursor-wait': isLoading,
    }
  );
}

/**
 * A less prominent button for "in-page" actions.
 *
 * Children are wrapped in a flex container by default, which is
 * offset upwards one pixel for better vertical centering.
 */
export const SmallButton = React.forwardRef(function SmallButton(
  {
    children,
    className = '',
    a11yLabel,
    type = 'button',
    appearance = 'default',
    isDisabled = false,
    isLoading = false,
    isHovered,
    isOnWhite = true,
    autoFocus,
    suffix,
    onClick = () => {},
    xPadding = 4,
    yPadding = 2,
    ['data-testid']: dataTestId,
  }: SmallButtonProps,
  ref?: React.MutableRefObject<HTMLButtonElement>
) {
  const handleOnClick = (event) => {
    if (isDisabled || isLoading) return;
    onClick?.(event);
  };

  const buttonClass = generateButtonClass({
    appearance: appearance,
    className: className,
    isDisabled: isDisabled,
    isLoading: isLoading,
    isHovered: isHovered,
    isOnWhite: isOnWhite,
    xPadding: xPadding,
    yPadding: yPadding,
  });

  const focusClass = cx({
    'text-grey-800': appearance === 'secondary',
    [className as string]: !!className && appearance === null,
  });

  return (
    <button
      ref={ref}
      className={buttonClass}
      aria-label={a11yLabel}
      type={type}
      disabled={isDisabled}
      onClick={handleOnClick}
      autoFocus={autoFocus}
      data-testid={dataTestId}
    >
      <FocusIndicator colorClass={focusClass} />
      <div className="flex-grow grid items-center justify-center small-button">
        <div className={cx({ invisible: !isLoading })}>
          <Spinner size="small" />
        </div>
        <div
          className={cx(
            { invisible: isLoading },
            'relative flex items-center justify-center'
          )}
        >
          {children}
        </div>
      </div>
      <div className="flex items-center flex-shrink-1">{suffix}</div>
    </button>
  );
});

export type SmallLinkButtonProps =
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    appearance?: SmallButtonAppearance;
    isDisabled?: boolean;
    isOnWhite?: boolean;
    target?: '_blank' | '_self' | '_parent' | '_top';
  };

/**
 * A link that is styled to look like a small button @see SmallButton
 */
export function SmallLinkButton({
  href,
  children,
  isOnWhite = true,
  isDisabled = false,
  appearance = 'default',
  onClick = () => {},
  target,
}: SmallLinkButtonProps) {
  const className = generateButtonClass({
    appearance: appearance,
    isDisabled: isDisabled,
    isLoading: false,
    isHovered: false,
    isOnWhite: isOnWhite,
    className:
      'flex items-center justify-center gap-2 inline-block hover:no-underline focus:no-underline nounderline',
  });

  return (
    <Link
      href={href}
      includeCommonStyles
      className={className}
      onClick={onClick}
      target={target}
    >
      {children}
    </Link>
  );
}
